// LineaHoraAHoraDialog — modal de detalle hora-a-hora de una línea (C1).
//
// Drill-down desde la grilla del dashboard del coordinador. READ-ONLY: el
// coordinador NO valida/edita acá (eso es del supervisor en S2). Muestra:
//   - Header: línea + turno + líder programado + KPI agregado del turno.
//   - Tabla hora-a-hora: 1 fila por bloque (hora, SKU, real, meta, cumplimiento,
//     paros, estado), ordenada por hora.
//   - Link "Ver análisis completo →" hacia C6 (drill-down que tenía LineaRow).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BloqueTurno, CoordinadorLineaDetalle, RegistroProduccionHora } from 'schemas';
import { generarBloquesTurno } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { useCoordinadorLineaDetalle, useCoordinadorTurnosList, useMe } from '../../lib/api/queries';
import { cn } from '../../lib/utils';
import { duracionBloqueMin } from '../captura/shared';
import { CorreccionRegistroDialog, type CeldaVaciaContexto } from './CorreccionRegistroDialog';
import { calcularHuecosCaptura } from './huecosCaptura';

/** Modo activo del modal de corrección (null = cerrado). */
type CorreccionModo =
  | { tipo: 'corregir'; registro: RegistroProduccionHora }
  | { tipo: 'alta'; celda: CeldaVaciaContexto };

/**
 * `cruza_medianoche` no viene en `CoordinadorLineaDetalle.turno` (el DTO solo
 * proyecta hora_inicio/hora_fin) — se deriva con la MISMA regla que el
 * backend usa para materializar ese campo en `turnos` (hora_fin < hora_inicio
 * ⇒ cruza medianoche). Necesario para alimentar `generarBloquesTurno`, la
 * única fuente de verdad de bloques (nunca recalcular +60 local).
 */
function turnoConCruce(turno: { hora_inicio: string; hora_fin: string }): {
  hora_inicio: string;
  hora_fin: string;
  cruza_medianoche: boolean;
} {
  return { ...turno, cruza_medianoche: turno.hora_fin < turno.hora_inicio };
}

/**
 * Bloques esperados del turno como `BloqueTurno[]` reales (contrato
 * compartido FE↔BE) — incluye los parciales de borde de turno. Para detectar
 * las celdas vacías (alta retroactiva) y el denominador de "Capturas".
 */
function bloquesEsperadosTurno(turno: { hora_inicio: string; hora_fin: string }): BloqueTurno[] {
  return generarBloquesTurno(turnoConCruce(turno));
}

function formatearMiles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(Math.round(n));
}

function toneCumplimiento(pct: number | null): 'ok' | 'warn' | 'crit' | 'neutral' {
  if (pct === null) return 'neutral';
  if (pct >= 95) return 'ok';
  if (pct >= 80) return 'warn';
  return 'crit';
}

/** Minutos de paro de un bloque = suma de minutos_perdidos de sus paros. */
function minutosParoBloque(r: RegistroProduccionHora): number {
  return (r.paros ?? []).reduce((acc, p) => acc + (p.minutos_perdidos ?? 0), 0);
}

function formatearDuracion(min: number): string {
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function estadoDotClass(estado: string | null | undefined): string {
  switch (estado) {
    case 'rojo':
      return 'bg-crit';
    case 'amarillo':
      return 'bg-warn';
    case 'verde':
      return 'bg-ok';
    default:
      return 'bg-fg-tertiary';
  }
}

/** Causal madre dominante de un bloque (la de mayor minutos). null si sin paros. */
function causalDominante(
  r: RegistroProduccionHora,
  causales: Record<string, { madre_nombre: string; especifica_nombre: string }>,
): string | null {
  const paros = r.paros ?? [];
  if (paros.length === 0) return null;
  const top = [...paros].sort((a, b) => (b.minutos_perdidos ?? 0) - (a.minutos_perdidos ?? 0))[0];
  if (!top) return null;
  return causales[top.causal_id]?.madre_nombre ?? 'Paro';
}

interface Props {
  /** Línea a mostrar. null → modal cerrado. */
  linea: { linea_id: string; linea_nombre: string } | null;
  /** Turno actual del dashboard (turno_actual_id). */
  turnoId: string | null;
  /** Fecha de hoy del dashboard (fecha_hoy). */
  fecha: string | null;
  onClose: () => void;
}

export function LineaHoraAHoraDialog({ linea, turnoId, fecha, onClose }: Props) {
  // Capacidad "Corrección de datos con motivo" — gate de visibilidad (el gate
  // duro vive en el API). Mismo patrón que `puede_configurar_tenant` en el FE.
  const me = useMe();
  const puedeCorregir = me.data?.ctx.puede_corregir_datos ?? false;

  // Selección interna de fecha + turno: arranca desde los props, pero quien
  // puede corregir puede moverla para ver/corregir un día/turno pasado.
  const [fechaSel, setFechaSel] = useState<string | null>(fecha);
  const [turnoSel, setTurnoSel] = useState<string | null>(turnoId);
  // Resetear la selección SOLO al abrir una línea distinta — NO en cada cambio
  // de los props fecha/turnoId. El Dashboard hace poll (refetchInterval 30s) y
  // re-pasa esos props; en el borde de la jornada operativa (p.ej. 07:00) la
  // fecha_hoy flipea 24→25 entre polls. Sincronizar con [fecha]/[turnoId]
  // pisaba la selección manual del usuario cada 30s ("el filtro no cambia").
  const lineaKey = linea?.linea_id ?? null;
  useEffect(() => {
    setFechaSel(fecha);
    setTurnoSel(turnoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineaKey]);

  const open = Boolean(linea) && Boolean(turnoSel);
  const { data, isLoading, isError, error } = useCoordinadorLineaDetalle(
    linea?.linea_id ?? null,
    turnoSel,
    fechaSel,
    { enabled: open },
  );

  // Turnos del coordinador — solo se consultan si puede corregir (para el selector).
  const turnos = useCoordinadorTurnosList();

  const [correccion, setCorreccion] = useState<CorreccionModo | null>(null);

  // El modal muestra el detalle del día actual (fecha = fecha_hoy del dashboard).
  // Propagamos ese contexto temporal al análisis completo para que NO abra con su
  // rango default (últimos 7 días). Si no hay fecha disponible, mandamos solo la
  // granularidad y dejamos que el detalle resuelva el rango — no inventamos fechas.
  const linkAnalisisParams = (() => {
    const params = new URLSearchParams();
    if (fechaSel) {
      params.set('desde', fechaSel);
      params.set('hasta', fechaSel);
    }
    params.set('granularidad', 'dia');
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  })();

  return (
    <>
      <Dialog open={open} onClose={onClose} className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <span
                className={cn(
                  'inline-block size-2 flex-shrink-0 rounded-full',
                  estadoDotClass(data?.kpi_linea?.estado_semaforo),
                )}
              />
              {linea?.linea_nombre ?? 'Detalle de línea'}
            </span>
          </DialogTitle>
          {data ? (
            <p className="text-sm text-fg-secondary">
              {data.turno.nombre} · {data.turno.hora_inicio}–{data.turno.hora_fin}
              {' · '}
              {data.programacion_lider.activa && data.programacion_lider.lider
                ? `${data.programacion_lider.lider.nombre} ${data.programacion_lider.lider.apellido}`
                : 'Sin líder'}
            </p>
          ) : null}

          {/* Selector de fecha + turno — solo para quien puede corregir. Permite
              moverse a un día/turno pasado; re-fetchea el detalle automáticamente. */}
          {puedeCorregir ? (
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">
                  Fecha
                </span>
                <Input
                  type="date"
                  value={fechaSel ?? ''}
                  onChange={(e) => setFechaSel(e.target.value || null)}
                  className="h-8 w-auto py-1 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">
                  Turno
                </span>
                <select
                  value={turnoSel ?? ''}
                  onChange={(e) => setTurnoSel(e.target.value || null)}
                  className={cn(
                    'flex h-8 w-auto rounded border border-border bg-surface-input px-2 py-1 text-sm text-fg',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent',
                  )}
                >
                  {turnos.data?.turnos.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nombre} · {t.hora_inicio}–{t.hora_fin}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}
        </DialogHeader>

        <DialogContent className="max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : isError ? (
            <p className="py-8 text-center text-sm text-crit">
              No se pudo cargar el detalle{error instanceof Error ? `: ${error.message}` : '.'}
            </p>
          ) : data ? (
            <div className="flex flex-col gap-4">
              {/* 4 KPI cards del turno */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiTurnoCard
                  label="Producido turno"
                  value={data.kpi_linea ? formatearMiles(data.kpi_linea.cantidad_real) : '0'}
                  suffix="paq"
                />
                <KpiTurnoCard
                  label="Cumpl · Meta"
                  value={
                    data.kpi_linea?.cumplimiento_pct != null
                      ? data.kpi_linea.cumplimiento_pct.toFixed(0)
                      : '—'
                  }
                  suffix={data.kpi_linea?.cumplimiento_pct != null ? '%' : ''}
                  tone={toneCumplimiento(data.kpi_linea?.cumplimiento_pct ?? null)}
                />
                <KpiTurnoCard
                  label="Paro"
                  value={
                    data.kpi_linea && data.kpi_linea.paros_min_totales > 0
                      ? formatearDuracion(data.kpi_linea.paros_min_totales)
                      : '—'
                  }
                  tone={data.kpi_linea && data.kpi_linea.paros_min_totales > 0 ? 'warn' : 'neutral'}
                />
                <KpiTurnoCard
                  label="Capturas"
                  value={String(data.kpi_linea?.bloques_capturados ?? data.bloques.length)}
                  suffix={`/ ${bloquesEsperadosTurno(data.turno).length}`}
                />
              </div>

              {/* Tabla hora-a-hora */}
              <BloquesHoraTable
                bloques={data.bloques}
                skus={data.skus}
                causales={data.causales}
                puedeCorregir={puedeCorregir}
                detalle={data}
                onCorregir={(registro) => setCorreccion({ tipo: 'corregir', registro })}
                onAlta={(celda) => setCorreccion({ tipo: 'alta', celda })}
              />

              {/* Footer: contexto + link al análisis completo (C6) */}
              {linea ? (
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                  <span className="text-[11px] uppercase tracking-wide text-fg-tertiary">
                    {data.linea.codigo} · {data.linea.area_nombre} · {data.linea.planta.nombre}
                  </span>
                  <Link
                    to={`/coordinador/linea/${linea.linea_id}${linkAnalisisParams}`}
                    className="text-sm font-medium text-accent hover:underline"
                    onClick={onClose}
                  >
                    Ver análisis completo →
                  </Link>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Modal de corrección / alta retroactiva (capacidad puede_corregir_datos). */}
      <CorreccionRegistroDialog modo={correccion} onClose={() => setCorreccion(null)} />
    </>
  );
}

function KpiTurnoCard({
  label,
  value,
  suffix,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  suffix?: string;
  tone?: 'ok' | 'warn' | 'crit' | 'neutral';
}) {
  const toneClass =
    tone === 'ok'
      ? 'text-ok'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'crit'
          ? 'text-crit'
          : 'text-fg';
  return (
    <div className="flex flex-col gap-1 rounded border border-border bg-surface-overlay/40 px-3 py-2.5">
      <span className="text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">
        {label}
      </span>
      <span className={cn('text-xl font-semibold tabular-nums', toneClass)}>
        {value}
        {suffix ? (
          <span className="ml-1 text-[11px] font-normal text-fg-tertiary">{suffix}</span>
        ) : null}
      </span>
    </div>
  );
}

function BloquesHoraTable({
  bloques,
  skus,
  causales,
  puedeCorregir,
  detalle,
  onCorregir,
  onAlta,
}: {
  bloques: RegistroProduccionHora[];
  skus: Record<string, { codigo: string; descripcion: string }>;
  causales: Record<string, { madre_nombre: string; especifica_nombre: string }>;
  puedeCorregir: boolean;
  detalle: CoordinadorLineaDetalle;
  onCorregir: (registro: RegistroProduccionHora) => void;
  onAlta: (celda: CeldaVaciaContexto) => void;
}) {
  // Sin capacidad de corregir y sin capturas → mensaje simple (comportamiento previo).
  if (bloques.length === 0 && !puedeCorregir) {
    return <p className="py-8 text-center text-sm text-fg-tertiary">Sin capturas en este turno.</p>;
  }

  // Orden operativo relativo al inicio del turno. Es indispensable en T3:
  // ordenar por el string HH:MM pondría 00:00 antes que 22:00 y dañaría el
  // acumulado aunque las filas luego se intercalaran visualmente.
  const inicioTurnoMin = (() => {
    const [hh, mm] = String(detalle.turno.hora_inicio).slice(0, 5).split(':').map(Number);
    return (hh ?? 0) * 60 + (mm ?? 0);
  })();
  const posEnTurno = (hhmm: string): number => {
    const [hh, mm] = hhmm.slice(0, 5).split(':').map(Number);
    return ((hh ?? 0) * 60 + (mm ?? 0) - inicioTurnoMin + 1440) % 1440;
  };
  const bloquesOrdenados = [...bloques].sort(
    (a, b) => posEnTurno(String(a.hora_inicio_bloque)) - posEnTurno(String(b.hora_inicio_bloque)),
  );

  // ¿Un solo SKU en todo el turno? → mostrarlo en el encabezado, no por fila.
  const skuIds = [...new Set(bloquesOrdenados.map((b) => b.sku_id))];
  const soloUnSku = skuIds.length === 1;
  const skuUnico = soloUnSku ? skus[skuIds[0]!] : null;
  const metaHora = soloUnSku
    ? (bloquesOrdenados.find((b) => b.capacidad_estandar_unidades_hora !== null)
        ?.capacidad_estandar_unidades_hora ?? null)
    : null;

  // Acumulados real/meta por fila (la "meta que lleva" del turno). INV-2: la
  // meta de CADA bloque se prorratea por su duración real (un parcial de 30'
  // vale la mitad), no se asume 1 bloque = 1 hora.
  let accReal = 0;
  let accMeta = 0;
  const filas = bloquesOrdenados.map((b) => {
    accReal += b.cantidad_procesada;
    accMeta += (b.capacidad_estandar_unidades_hora ?? 0) * (duracionBloqueMin(b) / 60);
    return { bloque: b, accReal, accMeta };
  });

  // Cobertura faltante = rangos no cubiertos dentro de las celdas esperadas.
  // Incluye tanto horas 100% vacías como huecos parciales entre tramos.
  const liderId = detalle.programacion_lider.activa
    ? (detalle.programacion_lider.lider?.id ?? null)
    : null;
  const celdasVacias = puedeCorregir
    ? calcularHuecosCaptura(detalle.turno, bloquesOrdenados).map<CeldaVaciaContexto>((hueco) => ({
        fecha: detalle.fecha,
        turno_id: detalle.turno.id,
        linea_id: detalle.linea.id,
        hora_inicio_bloque: hueco.inicio,
        hora_fin_bloque: hueco.fin,
        es_hueco_parcial: hueco.esParcial,
        lider_asignado_user_id: liderId,
      }))
    : [];

  // Nº de columnas (para el colSpan de filas vacías): Hora [+SKU] Detalle Real Cumpl Acum Estado [+Acción]
  const colsAccion = puedeCorregir ? 1 : 0;

  // Intercalar capturas y horas vacías en el ORDEN CRONOLÓGICO del turno
  // (minutos relativos a hora_inicio, módulo 24h): en un T3 22:00→06:00 la
  // fila de las 23:30 va antes que la de las 00:30, y las vacías quedan en
  // su lugar en vez de apiladas al final.
  type ItemTurno =
    | { tipo: 'captura'; pos: number; fila: (typeof filas)[number] }
    | { tipo: 'vacia'; pos: number; celda: CeldaVaciaContexto };
  const itemsOrdenados: ItemTurno[] = [
    ...filas.map<ItemTurno>((f) => ({
      tipo: 'captura',
      pos: posEnTurno(String(f.bloque.hora_inicio_bloque)),
      fila: f,
    })),
    ...celdasVacias.map<ItemTurno>((celda) => ({
      tipo: 'vacia',
      pos: posEnTurno(celda.hora_inicio_bloque),
      celda,
    })),
  ].sort((a, b) => a.pos - b.pos);

  return (
    <div className="overflow-x-auto rounded border border-border">
      <header className="flex items-center justify-between border-b border-border bg-surface-overlay/40 px-3 py-2">
        <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          Capturas hora a hora
        </span>
        {soloUnSku && skuUnico ? (
          <span className="text-[11px] text-fg-tertiary" title={skuUnico.descripcion}>
            SKU {skuUnico.codigo}
            {metaHora !== null ? ` · meta/h ${formatearMiles(metaHora)} paq` : ''}
          </span>
        ) : null}
      </header>
      <Table>
        <THead>
          <TR>
            <TH align="left">Hora</TH>
            {!soloUnSku ? <TH align="left">SKU</TH> : null}
            <TH align="left">Detalle</TH>
            <TH align="right">Real / meta</TH>
            <TH align="right">Cumpl.</TH>
            <TH align="right">Acum.</TH>
            <TH align="left">Estado</TH>
            {puedeCorregir ? <TH align="right">Acción</TH> : null}
          </TR>
        </THead>
        <TBody>
          {itemsOrdenados.map((item) =>
            item.tipo === 'captura' ? (
              <BloqueRow
                key={item.fila.bloque.id}
                bloque={item.fila.bloque}
                skus={skus}
                causales={causales}
                accReal={item.fila.accReal}
                accMeta={item.fila.accMeta}
                soloUnSku={soloUnSku}
                puedeCorregir={puedeCorregir}
                onCorregir={onCorregir}
              />
            ) : (
              <CeldaVaciaRow
                key={`vacia-${item.celda.hora_inicio_bloque}-${item.celda.hora_fin_bloque}`}
                celda={item.celda}
                soloUnSku={soloUnSku}
                colsAccion={colsAccion}
                onAlta={onAlta}
              />
            ),
          )}
        </TBody>
      </Table>
    </div>
  );
}

/** Fila de cobertura faltante — solo visible para quien puede corregir. */
function CeldaVaciaRow({
  celda,
  soloUnSku,
  colsAccion,
  onAlta,
}: {
  celda: CeldaVaciaContexto;
  soloUnSku: boolean;
  colsAccion: number;
  onAlta: (celda: CeldaVaciaContexto) => void;
}) {
  // Columnas entre "Hora" y "Acción": [SKU?] Detalle Real Cumpl Acum Estado
  const colsIntermedias = (soloUnSku ? 0 : 1) + 5;
  return (
    <TR className="bg-warn/5">
      <TD>
        <span className="font-mono text-sm tabular-nums text-fg-tertiary">
          {celda.hora_inicio_bloque}
        </span>
      </TD>
      <TD colSpan={colsIntermedias}>
        <span className="text-sm italic text-fg-tertiary">
          {celda.es_hueco_parcial ? 'Tramo sin captura' : 'Hora sin captura'}
        </span>
      </TD>
      {colsAccion > 0 ? (
        <TD align="right">
          <Button
            variant="outline"
            size="sm"
            className="whitespace-nowrap"
            title={
              celda.es_hueco_parcial ? 'Completar tramo faltante' : 'Agregar registro faltante'
            }
            onClick={() => onAlta(celda)}
          >
            Agregar
          </Button>
        </TD>
      ) : null}
    </TR>
  );
}

function BloqueRow({
  bloque,
  skus,
  causales,
  accReal,
  accMeta,
  soloUnSku,
  puedeCorregir,
  onCorregir,
}: {
  bloque: RegistroProduccionHora;
  skus: Record<string, { codigo: string; descripcion: string }>;
  causales: Record<string, { madre_nombre: string; especifica_nombre: string }>;
  accReal: number;
  accMeta: number;
  soloUnSku: boolean;
  puedeCorregir: boolean;
  onCorregir: (registro: RegistroProduccionHora) => void;
}) {
  const horaIni = String(bloque.hora_inicio_bloque).slice(0, 5);
  const sku = skus[bloque.sku_id];
  // INV-2: meta del bloque prorrateada por su duración real, no capacidad
  // horaria plana (un parcial de 30' tiene la mitad de meta).
  const capHora = bloque.capacidad_estandar_unidades_hora;
  const meta = capHora !== null && capHora > 0 ? capHora * (duracionBloqueMin(bloque) / 60) : null;
  const cumpl = meta !== null && meta > 0 ? (bloque.cantidad_procesada / meta) * 100 : null;
  const cumplTone = toneCumplimiento(cumpl);
  const minParo = minutosParoBloque(bloque);
  const causalMadre = causalDominante(bloque, causales);
  const invalidado = bloque.estado === 'invalidado';
  const validado = bloque.validado_at !== null;
  const tieneParo = minParo > 0;
  // Trazabilidad de corrección (datos ya vienen en el registro, sin query extra).
  // Los campos son opcionales (endpoints que no los proyectan ⇒ undefined).
  const esAltaCorreccion = bloque.origen_correccion === true;
  const esCorregido = Boolean(bloque.corregido_at);

  const cumplTextClass =
    cumplTone === 'ok'
      ? 'text-ok'
      : cumplTone === 'warn'
        ? 'text-warn'
        : cumplTone === 'crit'
          ? 'text-crit'
          : 'text-fg-tertiary';

  return (
    <TR className={cn(invalidado && 'opacity-60', tieneParo && 'bg-crit/5')}>
      <TD>
        <span className="font-mono text-sm tabular-nums text-fg">{horaIni}</span>
      </TD>
      {!soloUnSku ? (
        <TD>
          {sku ? (
            <span className="text-sm text-fg" title={sku.descripcion}>
              {sku.codigo}
            </span>
          ) : (
            <span className="text-sm text-fg-tertiary">—</span>
          )}
        </TD>
      ) : null}
      <TD>
        {tieneParo ? (
          <span className="text-sm text-crit">
            ⚠ {causalMadre} · {formatearDuracion(minParo)}
          </span>
        ) : (
          <span className="text-sm text-fg-tertiary">Operación normal</span>
        )}
      </TD>
      <TD align="right" numeric>
        <span className="text-sm font-medium tabular-nums text-fg">
          {formatearMiles(bloque.cantidad_procesada)}
        </span>
        {meta !== null ? (
          <span className="text-[11px] tabular-nums text-fg-tertiary">/{formatearMiles(meta)}</span>
        ) : null}
      </TD>
      <TD align="right" numeric>
        {cumpl !== null ? (
          <span className={cn('text-sm font-medium tabular-nums', cumplTextClass)}>
            {cumpl.toFixed(0)}%
          </span>
        ) : (
          <span className="text-fg-tertiary">—</span>
        )}
      </TD>
      <TD align="right" numeric>
        <span className="text-[11px] tabular-nums text-fg-tertiary">
          {formatearMiles(accReal)}
          {accMeta > 0 ? `/${formatearMiles(accMeta)}` : ''}
        </span>
      </TD>
      <TD>
        <span className="inline-flex flex-wrap items-center gap-1">
          {invalidado ? (
            <Badge tone="neutral">Invalidado</Badge>
          ) : validado ? (
            <Badge tone="ok">Validado</Badge>
          ) : (
            <Badge tone="warn">Pendiente</Badge>
          )}
          {esAltaCorreccion ? <Badge tone="info">Creado por corrección</Badge> : null}
          {esCorregido && !esAltaCorreccion ? (
            <Badge tone="info" title={bloque.razon_correccion ?? undefined}>
              Corregido
            </Badge>
          ) : null}
        </span>
      </TD>
      {puedeCorregir ? (
        <TD align="right">
          {!invalidado ? (
            <Button variant="outline" size="sm" onClick={() => onCorregir(bloque)}>
              Corregir
            </Button>
          ) : (
            <span className="text-xs text-fg-tertiary">—</span>
          )}
        </TD>
      ) : null}
    </TR>
  );
}
